import { useState } from "react";
import { TextInput, Button, InlineNotification } from "@carbon/react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "../../services/supabase";
import { establishApiSession } from "../../services/apiSession";
import { getSafeRedirectTarget } from "../../utils/redirect";

import styles from "./Login.module.scss";

const SITE_DOMAIN = import.meta.env.VITE_SITE_DOMAIN;
const AUTH_DOMAIN = import.meta.env.VITE_AUTH_DOMAIN;

type LoginProps = {
  onLoginSuccess: (user: User) => void;
};

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resetNotice, setResetNotice] = useState<"success" | "error" | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    // Authenticate with Supabase before creating the cookie session used by the website and API.
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setError(error.message);
      setSubmitting(false);
    } else {
      try {
        await establishApiSession(data.session.access_token);
      } catch {
        setError("Failed to establish session");
        setSubmitting(false);
        return;
      }

      // Continue only after both sessions are ready, using an allowed redirect destination.
      const redirectParam = new URLSearchParams(window.location.search).get("redirect_to");
      const redirectUrl = getSafeRedirectTarget(redirectParam || SITE_DOMAIN);
      window.location.href = redirectUrl;

      onLoginSuccess(data.user);
    }
  };

  return (
    <div className={styles.login}>
      <h2 className={styles.login__header}>Log in</h2>
      {error && (
        <InlineNotification
          kind="error"
          title="Login failed"
          subtitle={error ?? " "}
          lowContrast
          className={styles.login__errorNotice}
          onCloseButtonClick={() => setError(null)}
        />
      )}
      {resetNotice && (
        <InlineNotification
          kind={resetNotice === "success" ? "success" : "error"}
          title="Password reset"
          subtitle="If that email exists, we sent a reset link."
          lowContrast
          className={styles.login__resetNotice}
          onCloseButtonClick={() => setResetNotice(null)}
        />
      )}
      <form onSubmit={handleLogin}>
        <TextInput
          id="email"
          labelText="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className={styles.login__input}
        />
        <TextInput
          id="password"
          labelText="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className={styles.login__input}
        />
        <Button type="submit" disabled={submitting}>
          {submitting ? "Logging in…" : "Log in"}
        </Button>

        <Button
          kind="ghost"
          type="button"
          disabled={!email || submitting}
          onClick={async () => {
            try {
              await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${AUTH_DOMAIN}/set-password`,
              });
              setResetNotice("success");
            } catch {
              // Keep the same response whether or not the address belongs to an account.
              setResetNotice("success");
            }
          }}
          className={styles.login__resetButton}
        >
          Forgot password?
        </Button>
      </form>
    </div>
  );
};

export default Login;
